/** @module What the testers wrote: newest first, ticked off once read. */
import { Check, RotateCcw, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Segmented } from "@/components/ui/controls";
import { type Feedback, useFeedbackList, useMarkFeedback } from "@/features/admin/use-admin-scans";
import { dateTime } from "@/lib/format";

type Status = "open" | "done" | "all";

function Entry({ item }: { item: Feedback }) {
  const { t, i18n } = useTranslation();
  const mark = useMarkFeedback();
  const context = item.context as Record<string, unknown>;
  const where = [context.route, context.viewport, context.app_version]
    .filter((part) => typeof part === "string" && part !== "")
    .join(" · ");
  return (
    <li className="panel flex flex-col gap-2">
      <div className="row-between gap-2">
        <span className="row flex-wrap gap-2">
          <b>{item.user_name ?? "?"}</b>
          <span className="badge badge-neutral">{t(`feedback.kinds.${item.kind}`)}</span>
          {item.rating === 1 ? <ThumbsUp className="size-4 gain" /> : null}
          {item.rating === -1 ? <ThumbsDown className="size-4 loss" /> : null}
        </span>
        <span className="t-xs t-muted">
          {dateTime(item.created_at, i18n.resolvedLanguage ?? "de")}
        </span>
      </div>
      {item.message ? <p className="whitespace-pre-wrap">{item.message}</p> : null}
      <p className="t-xs t-muted break-words">
        {where}
        {typeof context.user_agent === "string" ? ` · ${context.user_agent}` : ""}
      </p>
      <button
        type="button"
        className="btn btn-secondary btn-sm self-start"
        disabled={mark.isPending}
        onClick={() => {
          mark.mutate({ id: item.id, done: item.done_at === null });
        }}
      >
        {item.done_at === null ? <Check /> : <RotateCcw />}
        {item.done_at === null ? t("admin.feedback.markDone") : t("admin.feedback.reopen")}
      </button>
    </li>
  );
}

export function FeedbackPanel() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("open");
  const page = useFeedbackList(status);
  const items = page.data?.pages.flatMap((one) => one.items) ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Segmented
        label={t("admin.feedback.filter")}
        value={status}
        onChange={setStatus}
        options={[
          { value: "open", label: t("admin.feedback.open") },
          { value: "done", label: t("admin.feedback.done") },
          { value: "all", label: t("admin.feedback.all") },
        ]}
      />
      {page.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
      {page.isError ? (
        <p role="alert" className="loss">
          {t("common.loadError")}
        </p>
      ) : null}
      {page.isSuccess && items.length === 0 ? (
        <p className="t-muted">{t("admin.feedback.empty")}</p>
      ) : null}
      <ul className="flex flex-col gap-3">
        {items.map((item) => (
          <Entry key={item.id} item={item} />
        ))}
      </ul>
      {page.hasNextPage ? (
        <button
          type="button"
          className="btn btn-secondary self-center"
          disabled={page.isFetchingNextPage}
          onClick={() => {
            void page.fetchNextPage();
          }}
        >
          {t("admin.scanList.more")}
        </button>
      ) : null}
    </div>
  );
}
